import React, { createRef, JSX } from "react";
import "../../../../App.css";
import LegacyApiClient from "../../../../Providers.Api/LegacyApiClient";
import { appContext } from "../../../../AppContext";
import { FileDropZone } from "@iconics/components";
import { IbssPage } from "../../../../Components/Core/BasePage/IbssPage";
import Helper from "../../../../Common/Helper";
import { IRouterProps, withRouting } from "@iconics/components";
import { DateTime } from "luxon";
import { NumberHelper } from "../../../../Common/NumberHelper";
import { SerendieSymbol } from "@serendie/symbols";
import { Button, ButtonBar, Card, DataGrid, DeleteButton, Dialog, DialogContent, DialogFooter, GridContainer, GridItem, IGridData, IGridRowsRequest, IGridRowsResponse, LayoutContainer, LayoutContent, RightButtons, TextField } from "@iconics/components";
import { AxiosResponse } from "axios";
import { Guid } from "@iconics/core";

class ListFiles extends IbssPage<IProps, IState>
{
    private readonly fileLastModifiedFormat = 'MM/dd/yyyy HH:mm:ss ZZ';
    private labels = appContext().labels;
    private get local() { return appContext().localStorageProvider; }

    private canCreate: boolean;
    private canDelete: boolean;
    private canRead: boolean;
    private canDownload: boolean;

    constructor(props: IProps)
    {
        super(props);
        this.state = {
            filesGrid: { rows: [], checkedIds: [] },
            filesRefreshToken: Guid.new().toString(),
            addModal: false,
            modalPathValue: "",
            modalSelectFileValue: null,
            selectedFileName: "",
            fileDropZoneResetToken: Guid.new().toString()
        };

        this.canCreate = this.local.hasRight('API.Files.Create');
        this.canDelete = this.local.hasRight('API.Files.Delete');
        this.canRead = this.local.hasRight('API.Files.Read');
        this.canDownload = this.local.hasRight('API.Files.Download');
    }

    public componentDidMount(): void
    {
        this.pageTitle = this.labels.Platform_FileManagement_PageTitle;
        this.setState({ modalPathValue: `${this.filesPath}/` });
    }

    private get filesPath(): string
    {
        const url = window.location.href;
        if (url.includes('roamer'))
        {
            return 'r';
        }
        else if (url.includes('unity'))
        {
            return 'u';
        }
        else if (url.includes('inspire'))
        {
            return 'i';
        }
        else if (url.includes('common'))
        {
            return 'c';
        }
        else
        {
            return '';
        }
    }

    private cancelModal(): void
    {
        this.setState({
            addModal: false,
            modalSelectFileValue: null,
            selectedFileName: "",
            modalPathValue: `${this.filesPath}/`,
            fileDropZoneResetToken: Guid.new().toString()
        });
    }

    private async handleDelete(): Promise<void>
    {
        if (this.canDelete)
        {
        await this.loading(async () =>
        {
            try 
            {
                const promises = this.checkedFileIds.map(id =>
                {
                    return LegacyApiClient.deleteFile(id);
                });

                await Promise.all(promises);
                this.setStateAsync({ filesRefreshToken: Guid.new().toString() });
            }
            catch (error) 
            {
                console.error(error);
            }
        });
    }
    }

    private async handleDownload(): Promise<void>
    {
        if (this.canDownload)
        {
        await this.loading(async () =>
        {
            try 
            {
                const promises = this.checkedFileIds.map(id =>
                {
                    return LegacyApiClient.downloadFile(id);
                });

                await Promise.all(promises);
            }
            catch (error) 
            {
                console.error(error);
            }
        });
    }
    }

    private changeFileHandler(file: File | null): void
    {
        if (file == null)
        {
            this.setState({
                modalSelectFileValue: null,
                selectedFileName: '',
                modalPathValue: `c/`
            });
        }
        else
        {
            this.setState({
                modalSelectFileValue: file,
                selectedFileName: file.name,
                modalPathValue: `${this.filesPath}/${file.name}`
            });
        }
    }

    private async handleModalOkButton(): Promise<void>
    {
        if (this.canCreate)
        {
        await this.loading(async () =>
        {
            try
            {
                if (this.state.modalSelectFileValue == null)
                {
                    return;
                }
                const formData = new FormData();
                formData.append("file", this.state.modalSelectFileValue);
                formData.append("fileName", this.state.selectedFileName);
                const fileDetail = {
                    fileData: formData,
                    modalPathValue: this.state.modalPathValue,
                    processAsMapvalue: false,
                };
                await LegacyApiClient.uploadFile(fileDetail)
                this.cancelModal();
                this.setState({ filesRefreshToken: Guid.new().toString() });
            }
            catch (error)
            {
                console.error(error)
            }
        });
    }

    }

    private get checkedFileIds(): string[]
    {
        return this.state.filesGrid.checkedIds ?? [];
    }

    private files?: IFile[];

    private async handleRowsRequest(request: IGridRowsRequest<IFile>): Promise<IGridRowsResponse<IFile>>
    {
        if (!this.canRead)
        {
            this.files = [];
        }
        else            
        {
            const response = await LegacyApiClient.getFiles2(this.filesPath)

            this.files = response.data.map(i =>
            ({
                id: `${i.path}/${i.name}`,
                lastModified: DateTime.fromFormat(i.lastModified, this.fileLastModifiedFormat),
                size: parseInt(i.size),
            }));
        }

        const sortedRows = DataGrid.sort(this.files, request.sorter);
        return { rows: sortedRows, nextSkipToken: '' };
    }

    public render(): JSX.Element
    {
        const pathAlreadyExists = this.state.filesGrid.rows.map(x => `${x.id}`).includes(this.state.modalPathValue);

        return (
            <>
                <LayoutContainer>
                    <LayoutContent>
                        <Card
                            icon={<SerendieSymbol name="file-upload" />}
                            title={this.labels.Platform_FileManagement_ListTitle}
                            subTitle={this.labels.Platform_FileManagement_ListSubTitle}
                        >
                            <GridContainer>
                                <GridItem>
                                    <ButtonBar>
                                        <RightButtons>
                                            <Button
                                                size='small'
                                                styleType='ghost'
                                                leftIcon={<SerendieSymbol name='download' />}
                                                onClick={() => this.handleDownload()}
                                                disabled={!this.canDownload || this.checkedFileIds.length === 0}
                                            >
                                                {this.labels.Platform_CommonAction_Download}
                                            </Button>
                                            <DeleteButton
                                                small={true}
                                                onDelete={() => this.handleDelete()}
                                                disabled={!this.canDelete || this.checkedFileIds.length === 0}
                                            />
                                            <Button
                                                size='small'
                                                styleType='ghost'
                                                leftIcon={<SerendieSymbol name='plus' />}
                                                onClick={() => this.setState({ addModal: true })}
                                                disabled={!this.canCreate || this.checkedFileIds.length > 0}
                                            >
                                                {this.labels.Platform_CommonAction_Add}
                                            </Button>
                                        </RightButtons>
                                    </ButtonBar>
                                </GridItem>
                                <GridItem>
                                    <DataGrid
                                        columns={[
                                            {
                                                columnId: Helper.nameOf<IFile>("id"),
                                                title: this.labels.File_Path,
                                                getCellValue: i => i.id,
                                                columnWidth: 50,
                                                sortable: true,
                                            },
                                            {
                                                columnId: Helper.nameOf<IFile>("lastModified"),
                                                title: this.labels.File_LastModified,
                                                getCellValue: i => i.lastModified.toLocaleDateTimeString(),
                                                columnWidth: 25,
                                                sortable: true,
                                            },
                                            {
                                                columnId: Helper.nameOf<IFile>("size"),
                                                title: this.labels.File_Size,
                                                getCellValue: i => NumberHelper.toDiskSize(i.size),
                                                columnWidth: 25,
                                                sortable: true,
                                            },
                                        ]}
                                        data={this.state.filesGrid}
                                        onChange={i => this.setState({ filesGrid: i })}
                                        rowId={i => i.id}
                                        onRowsRequest={i => this.handleRowsRequest(i)}
                                        refreshToken={this.state.filesRefreshToken}
                                        showCheckBoxes={true}
                                        allowOnlyOneCheckBox={true}
                                    />
                                </GridItem>
                            </GridContainer>
                        </Card>
                    </LayoutContent>
                </LayoutContainer>
                <Dialog
                    isOpen={this.state.addModal}
                    onOpenChange={() => this.cancelModal()}
                >
                    <DialogContent>
                        <Card
                            title={this.labels.Platform_AddFile_Title}
                        >
                            <GridContainer>
                                <GridItem>
                                    <FileDropZone
                                        existingFileNames={this.state.filesGrid.rows.map(x => x.id)}
                                        allowedFileTypes={{
                                            'image/jpeg': ['jpeg', 'jpg'],
                                            'image/png': ['png'],
                                            'image/svg+xml': ['svg'],
                                            'image/gif': ['gif'],
                                        }}
                                        maxFiles={1}
                                        previewFileOnDrop={true}
                                        onFilesDropped={files => this.changeFileHandler(files)}
                                        resetFilesToken={this.state.fileDropZoneResetToken}
                                    />
                                </GridItem>
                                <GridItem>
                                    <TextField
                                        label={this.labels.FileManagement_Path}
                                        value={this.state.modalPathValue}
                                        onChange={e => this.setState({ modalPathValue: e.target.value })}
                                        invalid={pathAlreadyExists}
                                        invalidMessage={pathAlreadyExists ? this.labels.FileManagement_PathExists_Message : ''}

                                    />
                                </GridItem>
                            </GridContainer>
                        </Card>
                    </DialogContent>
                    <DialogFooter>
                        <ButtonBar>
                            <RightButtons>
                                <Button
                                    styleType={'outlined'}
                                    onClick={() => this.cancelModal()}
                                >
                                    {this.labels.Platform_CommonAction_Cancel}
                                </Button>
                                <Button
                                    disabled={pathAlreadyExists || !this.state.modalSelectFileValue}
                                    onClick={() => this.handleModalOkButton()}
                                >
                                    {this.labels.Platform_AddFile_Upload}
                                </Button>
                            </RightButtons>
                        </ButtonBar>
                    </DialogFooter>
                </Dialog>
            </>
        );
    }

}

export default withRouting(ListFiles);

export interface IProps extends IRouterProps
{

}

export interface IState
{
    filesGrid: IGridData<IFile>;
    filesRefreshToken: string;
    addModal: boolean;
    modalPathValue: string;
    modalSelectFileValue: File | null;
    selectedFileName: string;
    fileDropZoneResetToken: string;
}

export interface IFile
{
    id: string,
    lastModified: DateTime;
    size: number;
}